// ── Tlhakiso Stokvel — pure helpers (no Firestore, easily testable) ──
//
// Tlhakiso Stokvel is a NON-CUSTODIAL transparency + reconciliation layer for
// savings groups. The platform NEVER holds or moves money — the group's funds stay
// in the group's OWN bank account. We record contributions/payouts and reconcile
// them against the (read-only) bank feed by each member's unique payment reference.

export const STOKVEL_TYPES = [
  { id: "savings", label: "Savings / rotating (ROSCA)" },
  { id: "grocery", label: "Grocery (year-end payout)" },
  { id: "burial", label: "Burial society" },
  { id: "investment", label: "Investment" },
  { id: "family", label: "Family / general contribution" },
];

export const FREQUENCIES = [
  { id: "monthly", label: "Monthly" },
  { id: "fortnightly", label: "Fortnightly" },
  { id: "weekly", label: "Weekly" },
  { id: "annual", label: "Annual" },
];

export function typeLabel(id) {
  const t = STOKVEL_TYPES.find((x) => x.id === id);
  return t ? t.label : (id || "—");
}
export function freqLabel(id) {
  const f = FREQUENCIES.find((x) => x.id === id);
  return f ? f.label : (id || "—");
}

// Money — South African Rand, sign-aware (a payout/withdrawal shows negative).
export function money(n) {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (v < 0 ? "-R " : "R ") + s;
}

// A transaction's receipts as an array (supports the legacy single `receipt`).
export function receiptsOfTxn(t) {
  if (!t) return [];
  if (Array.isArray(t.receipts)) return t.receipts.filter(Boolean);
  return t.receipt ? [t.receipt] : [];
}

// Verification state for a MONEY-OUT entry (expense / payout / cash withdrawal).
// "verified" requires BOTH the receipts reconciling to the full amount AND a
// second-person check. Bank-imported entries are self-evidenced by the statement.
export function verificationState(t) {
  if (!t) return { status: "verified" };
  if (t.source === "import" || t.source === "statementImport") return { status: "verified", bank: true };
  const amount = Number(t.amount) || 0;
  const recs = receiptsOfTxn(t);
  const receiptsSum = recs.reduce((s, r) => s + (Number(r && r.amount) || 0), 0);
  const outstanding = Math.max(0, amount - receiptsSum);
  const reconciled = recs.length > 0 && receiptsSum + 0.005 >= amount;
  const confirmed = !!t.verifiedBy;
  const status = reconciled && confirmed ? "verified" : "flagged";
  let reason = "";
  if (recs.length === 0) reason = "No receipt uploaded";
  else if (!reconciled) reason = money(outstanding) + " unaccounted";
  else if (!confirmed) reason = "Awaiting second-person check";
  return { status, receiptsSum, outstanding, reconciled, confirmed, reason, receipts: recs.length };
}

// Normalise a reference for matching: keep only A–Z and 0–9, uppercased. Bank
// narrations are noisy ("EFT CR MASA01 STOKVEL") so we compare on the clean token.
export function normRef(s) {
  return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

// Compose a one-line label from a group's structured bank details (for display).
// Falls back to any legacy free-text bankLabel.
export function bankLabelFrom(s) {
  if (!s) return "";
  const parts = [s.bankName, s.accountNumber, s.branchCode ? ("Branch " + s.branchCode) : ""].filter(Boolean);
  return parts.length ? parts.join(" · ") : (s.bankLabel || "");
}

// A short uppercase code derived from the group name — the prefix for member
// references (e.g. "Masakhane Stokvel" → "MASA").
export function codeFromName(name) {
  const clean = String(name || "").toUpperCase().replace(/[^A-Z0-9 ]/g, "").trim();
  if (!clean) return "STK";
  const words = clean.split(/\s+/).filter(Boolean);
  let code = words.length >= 2 ? words.map((w) => w[0]).join("") : words[0].slice(0, 4);
  code = code.replace(/[^A-Z0-9]/g, "").slice(0, 4);
  return code || "STK";
}

// The next free member reference: <CODE><NN>, zero-padded, skipping any already used.
export function nextMemberReference(stokvel, members) {
  const code = (stokvel && stokvel.code) || codeFromName(stokvel && stokvel.name);
  const used = new Set(Object.values(members || {}).map((m) => normRef(m && m.reference)));
  for (let i = 1; i < 1000; i++) {
    const ref = code + String(i).padStart(2, "0");
    if (!used.has(normRef(ref))) return ref;
  }
  return code + Date.now().toString().slice(-4);
}

// ── Burial-society claims register ──
// Only surfaced for stokvels of type 'burial'. A claim tracks a funeral payout from
// lodging through to payment, so the society keeps a stringent, per-member record of
// who was paid, for whom, and on what basis.
export const BURIAL_RELATIONSHIPS = [
  { id: "self", label: "Member (self)" },
  { id: "spouse", label: "Spouse" },
  { id: "child", label: "Child" },
  { id: "parent", label: "Parent" },
  { id: "sibling", label: "Sibling" },
  { id: "extended", label: "Extended family" },
  { id: "other", label: "Other dependant" },
];
export function relationshipLabel(id) { const t = BURIAL_RELATIONSHIPS.find((x) => x.id === id); return t ? t.label : (id || "—"); }

// Waiting period: cover for a person only becomes valid this many days AFTER they
// are added/changed (stops signing up someone already terminally ill and claiming
// immediately). Stored per-stokvel as `claimWaitingDays`; each dependant records a
// `coveredFrom` date. Changing a covered person = remove + re-add, which resets it.
export const DEFAULT_CLAIM_WAITING_DAYS = 180; // ~6 months
export const DEFAULT_COVERED_PER_MEMBER = 5;   // people each member may cover (society-set)

function _toDate(iso) { const d = new Date(String(iso) + "T00:00:00"); return isNaN(d.getTime()) ? null : d; }
function _isoOf(d) { return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); }

// Is a covered person active (past their waiting period) as at `onDateIso`?
// Returns { active, waitingUntil, daysLeft }.
export function coverStatus(coveredFrom, waitingDays, onDateIso) {
  const wd = Number(waitingDays) || 0;
  const from = _toDate(coveredFrom);
  if (!from || wd <= 0) return { active: true, waitingUntil: coveredFrom || null, daysLeft: 0 };
  const until = new Date(from.getTime() + wd * 86400000);
  const on = _toDate(onDateIso) || new Date();
  const active = on.getTime() >= until.getTime();
  return { active, waitingUntil: _isoOf(until), daysLeft: active ? 0 : Math.ceil((until.getTime() - on.getTime()) / 86400000) };
}

export const CLAIM_STATUSES = [
  { id: "lodged", label: "Lodged", tone: "warn" },
  { id: "approved", label: "Approved", tone: "info" },
  { id: "paid", label: "Paid", tone: "ok" },
  { id: "rejected", label: "Rejected", tone: "bad" },
];
export function claimStatusLabel(id) { const t = CLAIM_STATUSES.find((x) => x.id === id); return t ? t.label : (id || "—"); }
export function claimStatusTone(id) { const t = CLAIM_STATUSES.find((x) => x.id === id); return t ? t.tone : "info"; }

// A short, unambiguous invite code for member self-onboarding (shared as an app
// link AND texted to the WhatsApp bot). Excludes easily-confused characters
// (0/O, 1/I/L) so a member can read it aloud or type it without mistakes.
export function makeJoinCode(len = 6) {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

// Normalise a join code the same way whether it came from a link or a WhatsApp
// message: strip non-alphanumerics and uppercase (so "ab4 k9p" == "AB4K9P").
export function normJoinCode(s) {
  return String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

// ── Member reference schemes (chair-preset auto-generation) ──
// The chairperson chooses how new members' payment references are auto-generated.
export const REFERENCE_SCHEMES = [
  { id: "codeNumber", label: "Group code + number  (e.g. MS01)" },
  { id: "firstName", label: "First name  (e.g. Nosipho)" },
  { id: "codeName", label: "Code + first name  (e.g. MSNosipho)" },
];
export function referenceSchemeOf(stokvel) {
  return (stokvel && stokvel.referenceScheme) || "codeNumber";
}
// Generate the next reference for a new member per the group's scheme. Name-based
// schemes fall back to code+number until a name is entered. Always kept unique.
export function generateReference(stokvel, members, name, scheme) {
  const sc = scheme || referenceSchemeOf(stokvel);
  const code = (stokvel && stokvel.code) || codeFromName(stokvel && stokvel.name);
  const used = new Set(Object.values(members || {}).map((m) => normRef(m && m.reference)));
  const firstName = String(name || "").trim().split(/\s+/)[0] || "";
  const clean = firstName.replace(/[^A-Za-z0-9]/g, "");
  const uniquify = (base) => {
    if (!base) return nextMemberReference(stokvel, members);
    if (!used.has(normRef(base))) return base;
    for (let i = 2; i < 200; i++) { const c = base + i; if (!used.has(normRef(c))) return c; }
    return base + String(Date.now()).slice(-3);
  };
  if (sc === "firstName") return clean ? uniquify(clean) : nextMemberReference(stokvel, members);
  if (sc === "codeName") return clean ? uniquify(code + clean) : nextMemberReference(stokvel, members);
  return nextMemberReference(stokvel, members); // codeNumber (default)
}

// Match a bank transaction to a member by reference. Returns the member key
// (uid or "ref:<reference>") or null. We only ever auto-attribute money IN (credits).
export function matchTxnToMember(txn, members) {
  if (!txn || (Number(txn.amount) || 0) <= 0) return null; // credits only
  const hay = normRef(txn.description);
  if (!hay) return null;
  let best = null;
  Object.entries(members || {}).forEach(([key, m]) => {
    const ref = normRef(m && m.reference);
    if (ref && ref.length >= 3 && hay.includes(ref)) {
      // Prefer the longest matching reference (avoids MASA1 matching MASA10's haystack wrongly).
      if (!best || ref.length > best.refLen) best = { key, refLen: ref.length };
    }
  });
  return best ? best.key : null;
}

// Stable key for grouping contributions by member, whether or not they're an app user.
export function memberKey(c) {
  return c && c.memberUid ? c.memberUid : "ref:" + normRef(c && c.reference);
}

// Is a date within the current contribution period for this frequency?
export function isInCurrentPeriod(dateStr, frequency, now) {
  if (!dateStr) return false;
  const today = now || new Date();
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return false;
  if (frequency === "annual") return d.getFullYear() === today.getFullYear();
  if (frequency === "weekly") return (today - d) / 86400000 <= 7 && d <= today;
  if (frequency === "fortnightly") return (today - d) / 86400000 <= 14 && d <= today;
  // monthly (default)
  return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth();
}

// Per-member rollup: total contributed, count, last contribution, and whether they
// are up to date for the current period. `members` is the stokvel.members map.
export function memberSummaries(stokvel, contributions, now) {
  const members = (stokvel && stokvel.members) || {};
  const freq = (stokvel && stokvel.frequency) || "monthly";
  const byKey = {};
  // Seed every known member so even non-contributors show up. `openingBalance` is
  // the member's lifetime total contributed BEFORE they were migrated into the app
  // (a long-running stokvel doesn't start from zero) — it seeds their running total
  // but is kept separately so the UI can show "of which R X brought forward".
  Object.entries(members).forEach(([uid, m]) => {
    const opening = Number(m.openingBalance) || 0;
    byKey[uid] = { key: uid, uid, name: m.name || m.email || uid, email: m.email || "", role: m.role || "member", reference: m.reference || "", opening, total: opening, count: 0, last: null, upToDate: false };
  });
  (contributions || []).forEach((c) => {
    if (c.status === "void") return;
    const k = memberKey(c);
    if (!byKey[k]) {
      byKey[k] = { key: k, uid: c.memberUid || null, name: c.memberName || c.reference || "Unknown", email: "", role: "member", reference: c.reference || "", opening: 0, total: 0, count: 0, last: null, upToDate: false };
    }
    const row = byKey[k];
    row.total += Number(c.amount) || 0;
    row.count += 1;
    if (!row.last || String(c.date) > String(row.last.date)) row.last = { date: c.date, amount: Number(c.amount) || 0 };
    if (isInCurrentPeriod(c.date, freq, now)) row.upToDate = true;
  });
  return Object.values(byKey).sort((a, b) => b.total - a.total);
}

// ── LOANS (to members) + repayments, with optional interest ──
// Many stokvels lend the pot to members and earn interest back (interest is income
// that grows the group). We track it transparently; we never disburse or collect
// money — the group does that from its own account, and we record + reconcile it.

export const INTEREST_TYPES = [
  { id: "none", label: "No interest" },
  { id: "flat", label: "Flat once-off (% of principal)" },
  { id: "monthly", label: "Simple per month (% of principal / month)" },
  { id: "compound", label: "Compound per month (interest on interest)" },
];
export function interestTypeLabel(id) {
  const t = INTEREST_TYPES.find((x) => x.id === id);
  return t ? t.label : (id || "—");
}

// Whole months elapsed between an ISO date and a reference date.
export function monthsElapsed(fromStr, to) {
  const f = new Date(String(fromStr) + "T00:00:00");
  if (isNaN(f.getTime())) return 0;
  let m = (to.getFullYear() - f.getFullYear()) * 12 + (to.getMonth() - f.getMonth());
  if (to.getDate() < f.getDate()) m -= 1; // not a full month yet
  return Math.max(0, m);
}

// Interest accrued on a loan as at `asOf`.
//   flat     — once-off % of principal, time-independent.
//   monthly  — SIMPLE: rate% of principal per whole month (interest does NOT
//              itself earn interest).
//   compound — rate% per whole month, compounding: P·((1+r)^months − 1), so
//              interest earns interest. Only the interest portion is returned
//              (the principal is added back by callers).
// Per-month methods bill at least one month once an active loan is older than a day.
export function loanInterest(loan, asOf) {
  if (!loan) return 0;
  const P = Number(loan.principal) || 0;
  const rate = Number(loan.interestRate) || 0;
  const to = asOf || new Date();
  if (loan.interestType === "flat") return P * (rate / 100);
  if (loan.interestType === "monthly" || loan.interestType === "compound") {
    const issued = new Date(String(loan.issueDate) + "T00:00:00");
    const elapsed = monthsElapsed(loan.issueDate, to);
    const months = (loan.status === "active" && to > issued) ? Math.max(1, elapsed) : elapsed;
    if (months <= 0) return 0;
    if (loan.interestType === "compound") return P * (Math.pow(1 + rate / 100, months) - 1);
    return P * (rate / 100) * months; // simple
  }
  return 0; // 'none'
}

// Outstanding balance = principal + accrued interest − repayments (clamped at 0).
export function loanOutstanding(loan, repayments, asOf) {
  const repaid = (repayments || []).filter((r) => r.loanId === loan.id).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const gross = (Number(loan.principal) || 0) + loanInterest(loan, asOf);
  return Math.max(0, gross - repaid);
}

// Per-loan rollup with an effective status (auto-"settled" once fully repaid).
// Also exposes the agreed repayment PLAN (target count) + progress, so members can
// see "2 of 3 repayments made" and the expected total to settle. numberOfRepayments
// is the agreed plan only; outstanding is always the live principal+interest−paid,
// so a missed/partial payment is reflected honestly rather than forcing a schedule.
export function loanSummaries(loans, repayments, asOf) {
  const to = asOf || new Date();
  return (loans || []).map((loan) => {
    const mine = (repayments || []).filter((r) => r.loanId === loan.id);
    const repaid = mine.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const interest = loanInterest(loan, to);
    const expectedTotal = (Number(loan.principal) || 0) + interest; // to fully settle as at `to`
    const outstanding = Math.max(0, expectedTotal - repaid);
    const effectiveStatus = loan.status === "written_off" ? "written_off" : (outstanding <= 0.01 ? "settled" : "active");
    const targetCount = Number(loan.numberOfRepayments) || 0;
    return { ...loan, interest, repaid, expectedTotal, outstanding, effectiveStatus, targetCount, repaymentCount: mine.length };
  }).sort((a, b) => String(b.issueDate).localeCompare(String(a.issueDate)));
}

// Group-level totals. Recorded CASH position = contributions in − payouts out
// − loan principal disbursed + repayments received (incl. interest). Outstanding
// loans are a receivable; total worth = cash + outstanding loans.
//
// `opening` (optional) migrates a stokvel that existed BEFORE the app so it does
// not start from zero:
//   opening.cash         — the group's actual pot on the opening date (after years
//                          of past payouts/expenses, this is NOT just the sum of
//                          contributions, so it is tracked as its own figure).
//   opening.contribTotal — sum of every member's lifetime contributions brought
//                          forward (a recognition metric for "total contributions").
// Cash is built from opening.cash + only the movements RECORDED in the app, so the
// two never double-count. Both default to 0 → identical to the pre-migration behaviour.
// `expenses` (money OUT that isn't a payout or loan: bank charges, event/outing
// costs, other) and `income` (money IN that isn't a contribution: interest earned
// on the account, other income) both flow through cash. Both default to [] → the
// signature stays backward-compatible with older callers.
export function stokvelTotals(contributions, payouts, loans, repayments, asOf, opening, expenses, income) {
  const openingCash = Number(opening && opening.cash) || 0;
  const openingContrib = Number(opening && opening.contribTotal) || 0;
  const recordedIn = (contributions || []).filter((c) => c.status !== "void").reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const totalIn = openingContrib + recordedIn; // lifetime contributions (incl. brought-forward)
  const totalOut = (payouts || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const loansOut = (loans || []).filter((l) => l.status !== "written_off").reduce((s, l) => s + (Number(l.principal) || 0), 0);
  const repaidIn = (repayments || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const expensesOut = (expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const incomeIn = (income || []).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const sums = loanSummaries(loans, repayments, asOf);
  const outstandingLoans = sums.filter((l) => l.effectiveStatus === "active").reduce((s, l) => s + l.outstanding, 0);
  const cash = openingCash + recordedIn - totalOut - loansOut + repaidIn - expensesOut + incomeIn;
  return {
    totalIn, totalOut, loansOut, repaidIn, expensesOut, incomeIn, cash, outstandingLoans,
    openingCash, openingContrib, recordedIn,
    worth: cash + outstandingLoans,
    net: totalIn - totalOut,
    contributionCount: (contributions || []).filter((c) => c.status !== "void").length,
    payoutCount: (payouts || []).length,
    expenseCount: (expenses || []).length,
    incomeCount: (income || []).length,
    activeLoanCount: sums.filter((l) => l.effectiveStatus === "active").length,
  };
}

// Category labels for expenses & income (money out/in that isn't a contribution,
// payout or loan). Kept here so the native app and web share the vocabulary.
export const EXPENSE_CATEGORIES = [
  { id: "bank_charge", label: "Bank charges / fees" },
  { id: "cash_withdrawal", label: "Cash withdrawal" },
  { id: "purchase", label: "Purchase / supplier payment" },
  { id: "event", label: "Outing / gathering / event" },
  { id: "other", label: "Other expense" },
];
export const INCOME_CATEGORIES = [
  { id: "interest_earned", label: "Interest earned (bank)" },
  { id: "other", label: "Other income" },
];
export function expenseCategoryLabel(id) { const t = EXPENSE_CATEGORIES.find((x) => x.id === id); return t ? t.label : (id || "Expense"); }
export function incomeCategoryLabel(id) { const t = INCOME_CATEGORIES.find((x) => x.id === id); return t ? t.label : (id || "Income"); }

// Merge contributions, payouts, loan disbursements, repayments, expenses and
// income into one newest-first "movement of money" feed.
export function movementFeed(contributions, payouts, loans, repayments, limit, expenses, income) {
  const rows = [];
  (contributions || []).forEach((c) => { if (c.status !== "void") rows.push({ kind: "in", date: c.date, amount: Number(c.amount) || 0, who: c.memberName || c.reference || "Member", note: c.note || "", source: c.source || "manual", label: "Contribution" }); });
  (payouts || []).forEach((p) => rows.push({ kind: "out", date: p.date, amount: -(Number(p.amount) || 0), who: p.memberName || "Group", note: p.reason || "", source: "payout", label: "Payout" }));
  (loans || []).forEach((l) => rows.push({ kind: "out", date: l.issueDate, amount: -(Number(l.principal) || 0), who: l.memberName || "Member", note: l.note || "", source: "loan", label: "Loan disbursed" }));
  (repayments || []).forEach((r) => rows.push({ kind: "in", date: r.date, amount: Number(r.amount) || 0, who: r.memberName || "Member", note: r.note || "", source: r.source || "repayment", label: "Loan repayment" }));
  (expenses || []).forEach((e) => rows.push({ kind: "out", date: e.date, amount: -(Number(e.amount) || 0), who: "Group", note: e.note || "", source: "expense", label: expenseCategoryLabel(e.category) }));
  (income || []).forEach((i) => rows.push({ kind: "in", date: i.date, amount: Number(i.amount) || 0, who: "Group", note: i.note || "", source: "income", label: incomeCategoryLabel(i.category) }));
  rows.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  return limit ? rows.slice(0, limit) : rows;
}

// Given synced bank transactions, propose contributions for credits that match a
// member reference and aren't already recorded (by __bankTxnId). Used when the bank
// feed is live; harmless (returns []) while it's dormant and there are no txns.
export function proposeFromBank(transactions, stokvel, existingContributions) {
  const members = (stokvel && stokvel.members) || {};
  const seen = new Set((existingContributions || []).map((c) => c.__bankTxnId).filter(Boolean));
  const out = [];
  (transactions || []).forEach((t) => {
    if ((Number(t.amount) || 0) <= 0) return;       // credits only
    if (t.__bankTxnId && seen.has(t.__bankTxnId)) return;  // already recorded
    const key = matchTxnToMember(t, members);
    if (!key) return;
    const m = members[key] || {};
    out.push({
      memberUid: key.startsWith("ref:") ? null : key,
      memberName: m.name || m.email || "Member",
      reference: m.reference || "",
      date: t.date,
      amount: Number(t.amount) || 0,
      method: "bank",
      source: "bankFeed",
      __bankTxnId: t.__bankTxnId || null,
      note: (t.description || "").slice(0, 120),
    });
  });
  return out;
}
